/**
 * NotificationUnlockContentItem
 *
 * unlockContent — `notification_unlock_content_item.dart:42-156`.
 * Title: `{name} unlocked full video`. Subtitle is two lines:
 *   1. `<unlocked-icon> - {giftInfo.coins} <coin-icon>`
 *      (`giftInfo.coins` via `MathUtils.roundDoubleTwoDecimals`).
 *   2. `Total: {stats.coins} <coin-icon> · {date}`
 *      (`stats.coins` via `MathUtils.removeTrailingZero`).
 * Avatar from `usersInfo.userId`, trailing thumbnail from `mediaInfo`.
 */
import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { MathUtils } from '@/domain/utils/mathUtils';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { NotificationRowLayout } from '../NotificationRowLayout';
import { NotificationAvatar } from './shared/NotificationAvatar';
import { NotificationMediaThumbnail } from './shared/NotificationMediaThumbnail';
import { NotificationCoinsDateLine } from './shared/NotificationCoinsDateLine';
import type { NotificationItemProps } from './notificationItemProps';

export const NotificationUnlockContentItem: React.FC<NotificationItemProps> = ({
  notification,
  onUserTap,
  onItemTap,
  onItemMediaTap,
}) => {
  const { l10n } = useLocale();
  const { formatDate } = useDateFormatters();
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const n = l10n.inbox.notifications;

  const { info, stats } = notification;
  const mediaInfo = info.mediaInfo;
  const giftInfo = info.giftInfo;
  const name = info.usersInfo.name;

  const title = n.unlockedFullVideo.replace('{name}', name);

  const subtitle = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: responsive.gap.xs }}>
      {/* Line 1: unlocked icon - coins */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: responsive.gap.xs,
          color: theme.text.secondary,
          fontSize: responsive.font.sm,
        }}
      >
        <AssetIcon
          path="/assets/images/unlocked.png"
          width={responsive.icon.small}
          height={responsive.icon.small}
          color={theme.activeRed}
        />
        <span>- {MathUtils.roundDoubleTwoDecimals(giftInfo?.coins ?? 0)}</span>
        <AssetIcon
          path="/assets/images/coins.png"
          width={responsive.icon.xs}
          height={responsive.icon.xs}
          color={theme.exclusive}
        />
      </div>
      {/* Line 2: Total: stats.coins coin-icon · date */}
      <NotificationCoinsDateLine
        prefix={n.totalPrefix}
        amount={MathUtils.removeTrailingZero(stats?.coins ?? 0)}
        date={formatDate(info.timestamp)}
      />
    </div>
  );

  return (
    <NotificationRowLayout
      leading={<NotificationAvatar userId={info.usersInfo.userId} alt={name} />}
      title={title}
      subtitle={subtitle}
      trailing={mediaInfo ? <NotificationMediaThumbnail mediaInfo={mediaInfo} /> : undefined}
      onItemTap={onItemTap ? () => onItemTap(notification) : undefined}
      onLeadingTap={onUserTap ? () => onUserTap(info.usersInfo.userId) : undefined}
      onTrailingTap={onItemMediaTap ? () => onItemMediaTap(notification) : undefined}
    />
  );
};
